import { AddTreatmentRequestPayload } from "@/data/payload/treatment/add-treatment-request-payload";
import { AddTreatmentResponseEntity } from "@/domain/entity/treatment/add-treatment-response-entity";
import { TreatmentStepInfoEntity } from "@/domain/entity/treatment/treatment-step-info-entity";
import { TreatmentRepository } from "@/domain/repository/treatment-repository";
import { formatLocalToYyyyMmDd } from "@/utils/date-util";
import { SATError, SATResult } from "@sadtx/sat-error";
import { addDays } from "date-fns/addDays";

/** 판매 정책 제2조 8항 — "1개월"은 4주(28일) */
const WEEKS_PER_MONTH = 4;

/**
 * 치료 등록 — 기간은 구매한 이용권 상품의 개월 수로 정하되, 1개월 = 4주(28일)로 환산한다.
 * 종료일은 기존 14주 처방과 같은 방식(시작일 + 주차 × 7일)으로 잡아 항상 주차 경계에서 끝나고,
 * 서버의 `type`(치료 주차수)도 같은 주차 값을 보낸다. 서버 플랜·세션 로직은 시작일 기준 7일 블록이라 그대로 맞는다.
 *
 * consumeVoucher=true(스토어 구매 흐름): v2 API — 서버가 결제된 미사용 이용권을 요구하고 등록과 함께 소진한다.
 * consumeVoucher=false: 기존 v1 API — 이용권과 무관하게 등록 (기존 동작 유지).
 */
export class AddTreatmentUsecase {
  //MARK:- Constructor
  constructor(private readonly treatmentRepo: TreatmentRepository) {}

  //MARK:- execute
  async execute(
    patientUuid: string,
    memo: string,
    treatmentStepInfo: TreatmentStepInfoEntity | null,
    months: number,
    options: { consumeVoucher: boolean } = { consumeVoucher: false }
  ): Promise<SATResult<AddTreatmentResponseEntity, SATError>> {
    console.log("[AddTreatmentUsecase] :: execute :: patientUuid", patientUuid, "months", months, options);

    const startDate = new Date();
    const weeks = Math.max(1, months * WEEKS_PER_MONTH);
    const endDate = addDays(startDate, weeks * 7);

    const payload: AddTreatmentRequestPayload = {
      patientUuid,
      step: (treatmentStepInfo?.step ?? 0) + 1,
      type: weeks,
      startDate: formatLocalToYyyyMmDd(startDate),
      endDate: formatLocalToYyyyMmDd(endDate),
      memo,
      repeatIds: null,
    };

    console.log("[AddTreatmentUsecase] :: execute :: payload", payload);
    const result = options.consumeVoucher
      ? await this.treatmentRepo.addTreatmentWithVoucher(payload)
      : await this.treatmentRepo.addTreatment(payload);

    if (result.isFailure) {
      console.error("[AddTreatmentUsecase] :: execute :: error", result.error);
      return SATResult.failure(result.error);
    }

    console.log("[AddTreatmentUsecase] :: execute :: success");
    return SATResult.success(result.value!);
  }
}
